'use client';

import { forwardRef, useRef, useState, useImperativeHandle } from 'react';

import { cn } from '@fshr/shared-react';

interface OTPInputProps {
	pattern?: RegExp;
	length?: number;
	onChange: (event: any) => void;
	onBlur?: (event: any) => void;
	value?: string;
	errorMessage?: string;
	id?: string;
	name: string;
}

const hasChildNode = (childNodes?: NodeListOf<ChildNode>, nodeToFind?: Element | null): boolean => {
	if (!childNodes || !nodeToFind) return false;
	let hasNode = false;
	childNodes.forEach(node => {
		if (node === nodeToFind) {
			hasNode = true;
			return;
		}
	});
	return hasNode;
};

const OTPInput = forwardRef(
	(
		{
			id = 'otp-input',
			name,
			pattern = /\d/,
			length = 6,
			onChange,
			onBlur,
			value,
			errorMessage,
		}: OTPInputProps,
		ref: React.Ref<HTMLDivElement | null>
	) => {
		const [values, setValues] = useState(
			value ? value.split('') : Array.from({ length }, () => '')
		);
		const divRef = useRef<HTMLDivElement | null>(null);
		useImperativeHandle(ref, () => divRef.current as HTMLDivElement, []);

		const valueString = values.join('');

		const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
			if (!hasChildNode(divRef.current?.childNodes, event.relatedTarget)) {
				event.preventDefault();
				onBlur?.({
					currentTarget: {
						name,
						id,
						value: valueString,
					},
					data: {
						valuesArray: values,
					},
				});
			}
		};

		const handleChange = (index: number) => (evt: React.ChangeEvent<HTMLInputElement>) => {
			let fullValue = '';
			const { value } = evt.target;
			if (!pattern.test(value) && value !== '') {
				return;
			}

			if (value.length === length) {
				fullValue = value;
				setValues(value.split(''));
			} else {
				const updatedValues = [...values];
				updatedValues[index] = value.substring(0, 1);
				fullValue = updatedValues.join('');
				setValues(updatedValues);

				focusInput(index + 1);
			}

			onChange({
				currentTarget: {
					name,
					id,
					value: fullValue,
				},
				data: {
					valuesArray: fullValue.split(''),
				},
			});
		};

		const handleFocus = (evt: React.FocusEvent<HTMLInputElement>) => {
			evt.target.select();
		};

		const focusInput = (index: number) => {
			const inputEl = divRef?.current?.childNodes[index];
			if (inputEl) {
				(inputEl as HTMLInputElement).focus();
			}
		};

		return (
			<div className="flex flex-col gap-150">
				<div
					id={id}
					className={cn('flex flex-row gap-100', { 'text-red-700': errorMessage })}
					ref={divRef}
				>
					{values.map((value, index) => (
						<input
							key={index}
							type="text"
							className={cn(
								[
									'border',
									'border-border-default',
									'rounded-[5px]',
									'text-center',
									'w-[44px]',
									'h-[60px]',
									'text-h2',
									'hover:border-border-strong',
									'hover:outline-hidden',
									'focus:border-border-strong',
									'focus:outline-hidden',
									'valid:border-border-strong',
									'pl-[2px]',
									'lg:pl-[4px]',
									'pt-[8px]',
								],
								{
									'border-red-700 valid:border-red-700 hover:border-error-border focus:border-error-border':
										errorMessage,
								}
							)}
							value={value}
							inputMode="numeric"
							minLength={1}
							maxLength={length}
							pattern={pattern.source}
							onChange={handleChange(index)}
							onFocus={handleFocus}
							onBlur={handleBlur}
							required
						/>
					))}
				</div>
				{errorMessage && (
					<span
						className="formElement-message w-full text-center text-caption text-red-700"
						role="alert"
					>
						{errorMessage}
					</span>
				)}
			</div>
		);
	}
);

export default OTPInput;
